import React, { useEffect, useMemo } from "react"
import { Box, Typography } from "@mui/material"
import { dsColorNew } from '@palette'
import { dsRepricingDrawerData, dsRepricingDrawerErrors } from "Pages/DsRepricing/Page/@signals"
import { usePricelistCatalog } from "Pages/DsRepricing/Setup/@services/pricelists"
import { useProductsCatalog } from "Pages/DsRepricing/Setup/@services/useProducts"
import { DURATION_MAX_DAYS, bracketsFromProduct, rangeHasNoReference, referencesIn } from "Pages/DsRepricing/Page/@services/durations"
import RangeList from "Pages/DsRepricing/Page/@components/RangeList"

const NO_REFERENCE = 'no bracket’s reference length falls in this range — the engine prices nothing for it'

// Durations are LOR (Length Of Rental) windows in days. Both bounds inclusive.
//
// CU-869fkr1a9 (brief criteria 14-15, UX Obs. 7): nothing is prefilled and
// an empty list is valid — it means every bracket of the product, each
// searched at its reference length (brief Duda 2). The "Add" shortcuts are
// the brackets of the product's rows ("8–9 · ref 9"), recomputed when the
// product changes; a shortcut adds the whole bracket, the engine still
// searches its reference only. A range in which no bracket's reference
// falls is accepted and painted amber: the engine neither searches nor
// decides anything for it (criterion 24). Changing the product never
// deletes what the operator chose: ranges that no longer match stay amber.
export default function DsRepricingDurations() {
	const data = dsRepricingDrawerData.value
	const durations = data.durations ?? []
	const productId = data.productId ?? null
	const { items: pricelists, loading: pricelistsLoading } = usePricelistCatalog()
	const { items: products, loading: productsLoading } = useProductsCatalog()
	const product = productId == null ? null : (products || []).find(p => String(p.id) === String(productId)) || null
	const loading = pricelistsLoading || productsLoading
	const brackets = useMemo(() => bracketsFromProduct(product, pricelists), [product, pricelists])

	useEffect(() => {
		dsRepricingDrawerErrors.value = { ...dsRepricingDrawerErrors.value, durations: true }
	}, [])

	const warned = !loading && durations.some(d => rangeHasNoReference(d, brackets))
	const hint = productId == null
		? 'Pick a product in Basic, card 2: its brackets are offered here.'
		: loading
			? `Loading ${product?.name ? `${product.name}’s` : 'the product’s'} brackets…`
			: !durations.length
				? `Empty = all ${brackets.length ? `${brackets.length} ` : ''}brackets of ${product?.name || 'the product'}, each searched at its reference length.`
				: null

	return (
		<Box>
			<Typography sx={{ fontSize: 14, color: dsColorNew.textDark, fontWeight: 600 }}>
				Duration ranges (LOR)
			</Typography>
			<Typography component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted, display: 'block', mb: 1 }}>
				The rental lengths this rule applies to, in days.
			</Typography>
			<RangeList
				values={durations}
				onChange={next => {
					dsRepricingDrawerData.value = { ...dsRepricingDrawerData.value, durations: next }
				}}
				presets={{ singles: loading ? [] : brackets.map(b => ({ from: b.durationFrom, to: b.durationTo, label: b.label })) }}
				describeItem={(d) => {
					if (loading || !brackets.length) return null
					if (rangeHasNoReference(d, brackets)) return { warn: NO_REFERENCE }
					const refs = referencesIn(d, brackets)
					return { suffix: `· ref ${refs.join(', ')}` }
				}}
				fromKey="durationFrom" toKey="durationTo"
				fromPlaceholder="1" toPlaceholder="7"
				minValue={1} maxValue={DURATION_MAX_DAYS}
				unitSuffix="days"
				addLabel="Add duration"
				overlapsExisting={(existing, from, to) =>
					existing.some(d => from <= d.durationTo && d.durationFrom <= to)}
				overlapError="Overlaps with an existing duration range"
				showEmptyError={false}/>
			{warned && (
				<Typography component="span" sx={{ display: 'block', mt: 0.75, fontSize: 12, color: dsColorNew.noteAmber }}>
					Amber: {NO_REFERENCE}.
				</Typography>
			)}
			{hint && (
				<Typography component="span" sx={{ display: 'block', mt: 0.75, fontSize: 12, color: dsColorNew.textMuted }}>
					{hint}
				</Typography>
			)}
		</Box>
	)
}
